import { AxiosError, AxiosInstance } from 'axios';
import { auth } from '@/constants';
import { tokensApi } from '@/services/api/tokens';
import { authStore } from '@/store/auth';
import { INVALID_ACCESS_TOKEN_ERROR } from '@/constants';
import { store } from '@/store';
import { tokenProcessing } from '../base/utils';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import i18n from '@/settings/i18n';
import { flashMessageStore } from '@/store/flashMessage';

import { pages } from '@/settings/pages';

export const setUp = (instance: AxiosInstance) => {
  // MEMO: アクセストークンをヘッダに追加
  instance.interceptors.request.use((res) => {
    const state = store.getState();
    const isLoggedIn = state.auth.current.isLoggedIn;
    const authToken = state.auth.session?.token;

    return {
      ...res,
      headers: {
        ...res.headers,
        ...tryBuildAuthHeader(isLoggedIn, authToken),
      },
    };
  });

  /**
   * NOTE: アクセストークンの有効期限が切れたときに、トークンリフレッシュを実施
   * 再度ユーザーに認証情報を入力させず、アクセストークンを再発行する
   */
  createAuthRefreshInterceptor(instance, retryRequestWithRefreshedToken);

  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      if (err.response && err.response.status === 403) {
        window.location.assign(pages.signin.index);
      }

      if (err.response && err.response.status === 503) {
        store.dispatch(
          flashMessageStore.actions.set({
            body: i18n.t('ui.error.api.maintenance'),
            isWarning: true,
          }),
        );
        return;
      }

      return Promise.reject(err);
    },
  );
};

const adminAuthTokenKey = 'x-v2-admin-auth-token';

const tryBuildAuthHeader = (isLoggedIn: boolean, token: string | undefined) => {
  if (isLoggedIn) {
    return {
      [adminAuthTokenKey]: `Bearer ${token}`,
    };
  }

  return {};
};

const retryRequestWithRefreshedToken = async (err: AxiosError<any>) => {
  if (
    err.config?.headers &&
    err.config.headers[adminAuthTokenKey] &&
    err.response &&
    err.response.status === 401 &&
    err.response.data &&
    err.response.data.error === INVALID_ACCESS_TOKEN_ERROR
  ) {
    const state = store.getState();
    const refreshToken = state.auth.session?.refreshToken;
    const refreshTokenExpiresAt = state.auth.session?.refreshTokenExpiresAt;
    const isRefreshTokenExpired =
      !refreshToken ||
      !refreshTokenExpiresAt ||
      new Date(refreshTokenExpiresAt) < new Date();
    if (isRefreshTokenExpired) {
      window.location.assign(pages.signout.index);
      return;
    }

    try {
      const tokenResponse = await tokensApi.post({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: auth.CLIENT_ID,
      });
      const dispatch = store.dispatch;
      dispatch(authStore.actions.setSession(tokenProcessing(tokenResponse)));

      return Promise.resolve();
    } catch {
      // MEMO: リフレッシュトークン発行エラーの場合、ログアウト処理を行う
      window.location.assign(pages.signout.index);
      return;
    }
  } else {
    return Promise.reject(err);
  }
};
